<template>
  <transition 
    :name="transitionName" 
    @enter="enter"
    @leave="leave"
    @after-enter="afterEnter" 
    @after-leave="afterLeave"
  >
    <div v-if="visible" class="drawer-wrapper" @click="handleWrapperClick" :style="{ zIndex }">
      <div
        ref="drawer"
        class="drawer"
        :class="[
          `drawer--${direction}`,
          {
            'drawer--with-header': showHeader,
            'drawer--with-footer': showFooter
          }
        ]"
        :style="drawerStyle"
        @click.stop
      >
        <!-- 头部 -->
        <div v-if="showHeader" class="drawer__header">
          <slot name="header">
            <span class="drawer__title">{{ title }}</span>
          </slot>
          <button
            v-if="showClose"
            class="drawer__headerbtn"
            aria-label="Close"
            @click="handleClose"
          >
            <i class="drawer__close el-icon-close"></i>
          </button>
        </div>

        <!-- 内容区域 -->
        <div class="drawer__body">
          <slot></slot>
        </div>

        <!-- 底部 -->
        <div v-if="showFooter" class="drawer__footer">
          <slot name="footer">
            <el-button @click="handleCancel">{{ cancelText }}</el-button>
            <el-button type="primary" @click="handleConfirm">{{ confirmText }}</el-button>
          </slot>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  name: 'Drawer',
  props: {
    // 是否显示 drawer
    visible: {
      type: Boolean,
      default: false
    },
    // 标题
    title: {
      type: String,
      default: ''
    },
    // 方向
    direction: {
      type: String,
      default: 'rtl',
      validator: value => ['ltr', 'rtl', 'ttb', 'btt'].indexOf(value) !== -1
    },
    // 宽度/高度
    size: {
      type: [String, Number],
      default: '30%'
    },
    // 是否显示遮罩层
    modal: {
      type: Boolean,
      default: true
    },
    // 是否可以通过点击遮罩层关闭
    modalAppendToBody: {
      type: Boolean,
      default: false
    },
    // 是否可以通过按下 ESC 关闭
    closeOnPressEscape: {
      type: Boolean,
      default: true
    },
    // 是否可以通过点击遮罩层关闭
    closeOnClickModal: {
      type: Boolean,
      default: true
    },
    // 是否显示关闭按钮
    showClose: {
      type: Boolean,
      default: true
    },
    // 是否显示头部
    showHeader: {
      type: Boolean,
      default: true
    },
    // 是否显示底部
    showFooter: {
      type: Boolean,
      default: false
    },
    // 取消按钮文本
    cancelText: {
      type: String,
      default: '取消'
    },
    // 确认按钮文本
    confirmText: {
      type: String,
      default: '确定'
    },
    // 是否在打开时锁定 body 滚动
    lockScroll: {
      type: Boolean,
      default: true
    },
    // 自定义类名
    customClass: {
      type: String,
      default: ''
    },
    // 是否销毁子元素
    destroyOnClose: {
      type: Boolean,
      default: false
    },
    // 打开前的回调
    beforeClose: {
      type: Function,
      default: null
    },
    // 自定义 z-index
    zIndex: {
      type: Number,
      default: 2001
    }
  },
  data() {
    return {
      closed: false,
      bodyOverflow: null,
      bodyPaddingRight: null
    }
  },
  computed: {
    drawerStyle() {
      const style = {}
      if (this.direction === 'rtl' || this.direction === 'ltr') {
        style.width = typeof this.size === 'number' ? `${this.size}px` : this.size
      } else {
        style.height = typeof this.size === 'number' ? `${this.size}px` : this.size
      }
      return style
    },
    transitionName() {
      return `drawer-${this.direction}`
    }
  },
  watch: {
    visible(val) {
      if (val) {
        this.closed = false
        this.$emit('open')
        if (this.lockScroll) {
          this.afterOpen()
        }
        this.$nextTick(() => {
          if (this.closeOnPressEscape) {
            document.addEventListener('keydown', this.handleKeydown)
          }
        })
      } else {
        if (this.lockScroll) {
          this.afterClose()
        }
        document.removeEventListener('keydown', this.handleKeydown)
        this.$emit('close')
      }
    }
  },
  mounted() {
    if (this.visible) {
      this.afterOpen()
    }
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.handleKeydown)
    this.afterClose()
  },
  methods: {
    afterEnter() {
      this.$emit('opened')
    },
    afterLeave() {
      this.$emit('closed')
      this.closed = true
    },
    afterOpen() {
      this.bodyOverflow = document.body.style.overflow
      document.body.style.overflow = 'hidden'
    },
    afterClose() {
      if (this.bodyOverflow !== null) {
        document.body.style.overflow = this.bodyOverflow
        this.bodyOverflow = null
      }
    },
    handleWrapperClick() {
      if (this.closeOnClickModal) {
        this.handleClose()
      }
    },
    handleClose() {
      if (typeof this.beforeClose === 'function') {
        this.beforeClose(this.hide)
      } else {
        this.hide()
      }
    },
    hide(cancel) {
      if (cancel !== false) {
        this.$emit('update:visible', false)
        this.$emit('close')
      }
    },
    handleKeydown(e) {
      if (e.keyCode === 27) {
        this.handleClose()
      }
    },
    handleCancel() {
      this.$emit('cancel')
      this.handleClose()
    },
    handleConfirm() {
      this.$emit('confirm')
    },
    enter(el) {
      // 实现进入动画
      const drawer = this.$refs.drawer
      if (drawer) {
        // 设置初始位置
        if (this.direction === 'rtl') {
          drawer.style.transform = 'translateX(100%)'
        } else if (this.direction === 'ltr') {
          drawer.style.transform = 'translateX(-100%)'
        } else if (this.direction === 'ttb') {
          drawer.style.transform = 'translateY(-100%)'
        } else if (this.direction === 'btt') {
          drawer.style.transform = 'translateY(100%)'
        }
        
        // 强制重绘
        drawer.offsetHeight
        
        // 设置过渡动画
        drawer.style.transition = 'transform 0.3s ease-in-out'
        
        // 执行动画
        this.$nextTick(() => {
          if (this.direction === 'rtl' || this.direction === 'ltr') {
            drawer.style.transform = 'translateX(0)'
          } else {
            drawer.style.transform = 'translateY(0)'
          }
        })
      }
    },
    leave(el) {
      // 实现离开动画
      const drawer = this.$refs.drawer
      if (drawer) {
        // 设置过渡动画
        drawer.style.transition = 'transform 0.3s ease-in-out'
        
        // 执行动画
        if (this.direction === 'rtl') {
          drawer.style.transform = 'translateX(100%)'
        } else if (this.direction === 'ltr') {
          drawer.style.transform = 'translateX(-100%)'
        } else if (this.direction === 'ttb') {
          drawer.style.transform = 'translateY(-100%)'
        } else if (this.direction === 'btt') {
          drawer.style.transform = 'translateY(100%)'
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.drawer-wrapper {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  overflow: auto;
  margin: 0;
  background-color: rgba(0, 0, 0, 0.5);
  .drawer {
    position: absolute;
    background: #fff;
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
    display: flex;
    flex-direction: column;
    box-sizing: border-box;

    &--rtl {
      top: 0;
      right: 0;
      bottom: 0;
    }

    &--ltr {
      top: 0;
      left: 0;
      bottom: 0;
      .drawer__headerbtn {
        font-size: 27px;
        color: #fff;
        transform: translateX(100%);
        right: -20px;
      }
    }

    &--ttb {
      top: 0;
      left: 0;
      right: 0;
    }

    &--btt {
      bottom: 0;
      left: 0;
      right: 0;
    }

    &--with-header {
      .drawer__body {
        padding-top: 0;
      }
    }

    &--with-footer {
      .drawer__body {
        padding-bottom: 0;
      }
    }
  }

  .drawer__header {
    padding: 22px 30px 15px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    box-sizing: border-box;
  }

  .drawer__title {
    margin: 0;
    font-size: 18px;
    line-height: 1.33333;
    color: #303133;
    font-weight: 500;
  }

  .drawer__headerbtn {
    position: absolute;
    top: 20px;
    right: 30px;
    z-index: 1;
    padding: 0;
    background: transparent;
    border: none;
    outline: none;
    cursor: pointer;
    font-size: 16px;
    color: #909399;

    &:hover {
      color: #409eff;
    }
  }

  .drawer__close {
    font-weight: bold;
  }

  .drawer__body {
    flex: 1;
    padding: 0 30px;
    overflow: auto;
    box-sizing: border-box;
  }

  .drawer__footer {
    padding: 10px 30px 20px;
    text-align: right;
    box-sizing: border-box;

    .el-button + .el-button {
      margin-left: 10px;
    }
  }

  // 过渡动画 - 只保留遮罩层的淡入淡出
  .drawer-rtl-enter-active,
  .drawer-rtl-leave-active,
  .drawer-ltr-enter-active,
  .drawer-ltr-leave-active,
  .drawer-ttb-enter-active,
  .drawer-ttb-leave-active,
  .drawer-btt-enter-active,
  .drawer-btt-leave-active {
    transition: opacity 0.3s ease-in-out;
  }

  .drawer-rtl-enter,
  .drawer-rtl-leave-to,
  .drawer-ltr-enter,
  .drawer-ltr-leave-to,
  .drawer-ttb-enter,
  .drawer-ttb-leave-to,
  .drawer-btt-enter,
  .drawer-btt-leave-to {
    opacity: 0;
  }

  .drawer-rtl-enter-to,
  .drawer-rtl-leave,
  .drawer-ltr-enter-to,
  .drawer-ltr-leave,
  .drawer-ttb-enter-to,
  .drawer-ttb-leave,
  .drawer-btt-enter-to,
  .drawer-btt-leave {
    opacity: 1;
  }
}
</style> 